import { useState } from 'react';
import { Avatar, Logo } from './ui/bits.jsx';
import { Icon } from './ui/Icon.jsx';
import { InstallButton, InstallHelpModal } from './InstallPrompt.jsx';
import { useTheme, useOnline } from '../hooks.js';
import { fullName } from '../lib/format.js';
import { useSola } from '../sola.jsx';
import { t } from '../i18n/index.js';

/** Lepljiva glava, skupna zaslonu tečajnika in inštruktorja. */
export function AppHeader({ profile, role, onSignOut, onProfile, onBack, extra }) {
  const [help, setHelp] = useState(false);
  const online = useOnline();
  const { sola } = useSola();

  return (
    <>
      <header className="appbar">
        {/* Viden gumb za nazaj, kadar smo „v globini" (detajl, reševanje
            testa). Isto stori tudi sistemski gumb na telefonu. */}
        {onBack && (
          <button className="btn-icon" onClick={onBack} aria-label={t('Nazaj')} title={t('Nazaj')}>
            <Icon name="left" size={20} />
          </button>
        )}

        <span className="row" style={{ gap: 'var(--s-2)', minWidth: 0 }}>
          <Logo height={30} />
          <span className="wordmark truncate" title={sola?.ime} hidden={!sola?.logo_url && !!sola?.id}>
            {sola?.kratko || sola?.ime || 'Učilnica'}
          </span>
        </span>

        <div className="spacer" />

        {!online && (
          <span className="badge badge--wait" title={t('Brez povezave')}>
            <Icon name="wind" size={14} />
            Offline
          </span>
        )}

        {extra}

        <InstallButton onNeedHelp={() => setHelp(true)} compact />
        <ThemeToggle />

        <span className="row" style={{ gap: 'var(--s-2)' }}>
          {onProfile ? (
            <button
              className="row"
              onClick={onProfile}
              style={{ gap: 'var(--s-2)', padding: 0 }}
              title={t('Moji podatki')}
            >
              <Avatar ime={profile?.ime} priimek={profile?.priimek} size={32} />
              <span className="hide-narrow" style={{ minWidth: 0, textAlign: 'left' }}>
                <span className="t-sm t-bold truncate" style={{ display: 'block', maxWidth: 130 }}>
                  {fullName(profile) ?? role}
                </span>
                <span className="t-xs t-dimmer">{t('Moji podatki')}</span>
              </span>
            </button>
          ) : (
            <>
              <Avatar ime={profile?.ime} priimek={profile?.priimek} size={32} />
              <span className="hide-narrow" style={{ minWidth: 0 }}>
                <span className="t-sm t-bold truncate" style={{ display: 'block', maxWidth: 130 }}>
                  {fullName(profile) ?? role}
                </span>
                <span className="t-xs t-dimmer">{role}</span>
              </span>
            </>
          )}
          <button className="btn-icon btn-icon--sm" onClick={onSignOut} aria-label={t('Odjava')} title={t('Odjava')}>
            <Icon name="logout" size={18} />
          </button>
        </span>
      </header>

      {help && <InstallHelpModal onClose={() => setHelp(false)} />}
    </>
  );
}

const NEXT = { light: 'dark', dark: 'light' };
const ICON = { light: 'moon', dark: 'sun' };
const NAME = { light: 'Preklopi na temno', dark: 'Preklopi na svetlo' };

function ThemeToggle() {
  const [theme, setTheme] = useTheme();
  return (
    <button
      className="btn-icon btn-icon--sm"
      onClick={() => setTheme(NEXT[theme])}
      title={t(NAME[theme])}
      aria-label={t(NAME[theme])}
    >
      <Icon name={ICON[theme]} size={18} />
    </button>
  );
}

/** Kazalnik za potegni-za-osvežitev. */
export function PullIndicator({ distance, busy }) {
  if (distance <= 0 && !busy) return null;
  return (
    <div
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        display: 'flex',
        justifyContent: 'center',
        paddingTop: 'var(--s-3)',
        zIndex: 'var(--z-toast)',
        pointerEvents: 'none',
      }}
    >
      <span className="badge badge--brand" style={{ boxShadow: 'var(--sh-2)', padding: '6px 14px' }}>
        <Icon name="refresh" size={14} />
        {busy ? t('Osvežujem …') : distance > 60 ? t('Spusti za osvežitev') : t('Potegni')}
      </span>
    </div>
  );
}

/** Glavna navigacija: zavihki na širokem, pas na dnu na telefonu. */
export function MainNav({ items, value, onChange, label }) {
  return (
    <nav className="mainnav" role="tablist" aria-label={label}>
      {items.map((it) => (
        <button
          key={it.id}
          className="mainnav__item"
          role="tab"
          aria-selected={value === it.id}
          aria-controls={`panel-${it.id}`}
          id={`tab-${it.id}`}
          onClick={() => onChange(it.id)}
        >
          <Icon name={it.icon} size={20} />
          <span>{it.label}</span>
          {it.badge > 0 && <span className="mainnav__badge num">{it.badge}</span>}
        </button>
      ))}
    </nav>
  );
}

/**
 * Podrazdelki znotraj zavihka (npr. Knjižica: Leti | Dnevi).
 *
 * Glavna navigacija ima največ pet mest — toliko palec zanesljivo zadene
 * na dnu telefona. Vse, kar sodi skupaj, je zato združeno v en zavihek
 * s segmenti. Segmenti so prav tako zavihki (role="tab") v svojem seznamu.
 */
export function Segments({ items, value, onChange, label }) {
  return (
    <div className="segments" role="tablist" aria-label={label}>
      {items.map((it) => (
        <button
          key={it.id}
          className="segments__item"
          role="tab"
          aria-selected={value === it.id}
          id={`seg-${it.id}`}
          onClick={() => onChange(it.id)}
        >
          {it.icon && <Icon name={it.icon} size={16} />}
          <span>{it.label}</span>
          {it.badge > 0 && <span className="mainnav__badge num">{it.badge}</span>}
        </button>
      ))}
    </div>
  );
}
